import { Fragment, useEffect, useState } from "react";
import { api, money, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FlowStrip } from "@/components/FlowStrip";
import { Workflow, Search } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InvoicesSection } from "@/components/InvoicesSection";

const LABELS = { quotation: "Quotation", invoice: "Invoice", delivery_note: "Delivery Note", statement: "Statement" };

export default function DocumentFlow() {
  const [flows, setFlows] = useState([]);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") === "invoices" ? "invoices" : "flows";
  const load = () => api.get("/flows").then(({ data }) => setFlows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const visible = flows.filter((f) => {
    if (!q) return true;
    const hay = [f.customer?.company_name, f.customer?.contact_person, ...Object.values(f.steps).map((s) => s.number)].join(" ").toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <div>
      <PageHeader title="Document Flow" subtitle="Quotation → Invoice → Delivery Note → Statement. Create the next document in one click.">
        <Button onClick={() => navigate("/quotations/new")} className="bg-primary hover:bg-[#C81219] text-white" data-testid="flow-new-quote-button"><Workflow className="h-4 w-4 mr-1.5" /> Start with a Quotation</Button>
      </PageHeader>
      <Tabs value={tab} onValueChange={(v) => setSp(v === "invoices" ? { tab: "invoices" } : {})} className="mb-4">
        <TabsList data-testid="docflow-tabs">
          <TabsTrigger value="flows" data-testid="docflow-tab-flows">Document chains</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="docflow-tab-invoices">Invoices</TabsTrigger>
        </TabsList>
      </Tabs>
      {tab === "invoices" ? <InvoicesSection /> : <>
      <div className="relative max-w-sm mb-4"><Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client or document number…" className="pl-9" data-testid="flow-search" /></div>
      <div className="bg-white border border-border rounded-md">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Client</TableHead><TableHead>Started</TableHead><TableHead className="text-right">Value</TableHead>
            <TableHead>Progress</TableHead><TableHead>Next step</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {visible.map((f) => (
              <Fragment key={f.id}>
                <TableRow className="cursor-pointer hover:bg-secondary/40" onClick={() => setOpenId(openId === f.id ? null : f.id)} data-testid={`flow-row-${f.id}`}>
                  <TableCell><div className="font-medium">{f.customer?.company_name || f.customer?.contact_person || "—"}</div><div className="text-xs text-muted-foreground">{f.steps.quotation.number || ""}{f.steps.invoice.number ? ` · ${f.steps.invoice.number}` : ""}</div></TableCell>
                  <TableCell className="text-sm">{f.date ? fmtDate(f.date) : "—"}</TableCell>
                  <TableCell className="text-right tabular">{money(f.total || 0)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1" data-testid={`flow-progress-${f.id}`}>
                      {Object.keys(LABELS).map((k) => <span key={k} title={LABELS[k]} className={`h-2 w-6 rounded-sm ${f.steps[k].done ? "bg-emerald-600" : "bg-zinc-200"}`} />)}
                      <span className="text-xs text-muted-foreground ml-1">{f.progress}/4</span>
                    </div>
                  </TableCell>
                  <TableCell>{f.next_step ? <Badge variant="outline" className="border-primary text-primary">{LABELS[f.next_step]}</Badge> : <Badge variant="outline" className="border-emerald-600 text-emerald-700">Complete</Badge>}</TableCell>
                </TableRow>
                {openId === f.id && <TableRow><TableCell colSpan={5} className="bg-secondary/30 p-4"><FlowStrip kind={f.anchor_kind} id={f.anchor_id} onChanged={load} /></TableCell></TableRow>}
              </Fragment>
            ))}
            {visible.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">No document chains yet. Start with a quotation.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      </>}
    </div>
  );
}
